import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfBadge, EmptyState, PageHeader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const FRAMEWORKS = ["panchatanthra", "viveka_kartha", "dinacharya"];
const PT_ELEMENTS = [
  { code: "REV", label: "Review Mechanism" },
  { code: "FIN", label: "Financial Performance" },
  { code: "LEAD", label: "Leadership Effectiveness" },
  { code: "BMOD", label: "Business Model" },
  { code: "TRUST", label: "Trust & Brand Equity" },
];

export default function AdminRoles() {
  const { tenantId, tenant } = useAuth();
  const [grants, setGrants] = useState([]);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", role: "ceo_leadership", frameworks: [], element_codes: [], confidentiality_max: "1" });
  const [newUser, setNewUser] = useState({ email: "", name: "", title: "", password: "Portal@2026" });

  const load = () => {
    if (!tenantId) return;
    api.get(`/tenants/${tenantId}/grants`).then(({ data }) => setGrants(data)).catch((e) => toast.error(errMsg(e)));
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get("/meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, [tenantId]); // eslint-disable-line

  const createGrant = async () => {
    try {
      await api.post(`/tenants/${tenantId}/grants`, {
        user_id: form.user_id,
        tenant_id: tenantId,
        role: form.role,
        frameworks: form.frameworks,
        element_codes: form.element_codes,
        org_node_ids: [],
        confidentiality_max: Number(form.confidentiality_max),
        actions: [],
      });
      toast.success("Scoped grant issued");
      setOpen(false);
      setForm({ user_id: "", role: "ceo_leadership", frameworks: [], element_codes: [], confidentiality_max: "1" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const createUser = async () => {
    try {
      await api.post("/users", newUser);
      toast.success("User provisioned");
      setUserOpen(false);
      setNewUser({ email: "", name: "", title: "", password: "Portal@2026" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="space-y-10">
      <PageHeader
        testid="admin-roles-header"
        eyebrow="Administration"
        title="Roles & scoped permission matrix"
        description={`Grants are additive but always bounded by tenant, framework, org scope and confidentiality. ${tenant?.name || ""}`}
        actions={
          <div className="flex flex-wrap gap-3">
            <Dialog open={userOpen} onOpenChange={setUserOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-user-button" variant="outline" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  Provision user
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display text-2xl">Provision user</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  {[["name", "Full name"], ["email", "Work email"], ["title", "Title"], ["password", "Initial password"]].map(([k, l]) => (
                    <div key={k} className="space-y-2">
                      <Label className="label-caps">{l}</Label>
                      <Input data-testid={`new-user-${k}-input`} className="rounded-none" value={newUser[k]} onChange={(e) => setNewUser({ ...newUser, [k]: e.target.value })} />
                    </div>
                  ))}
                  <Button data-testid="create-user-submit" className="w-full rounded-none" disabled={!newUser.email || !newUser.name} onClick={createUser}>
                    Create user
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-grant-button" className="h-9 rounded-none text-[10px] uppercase tracking-[0.18em]">
                  Issue grant
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader><DialogTitle className="font-display text-2xl">Scoped grant</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="label-caps">User</Label>
                    <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                      <SelectTrigger data-testid="grant-user-select" className="rounded-none"><SelectValue placeholder="Select user" /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Role</Label>
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="grant-role-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {(meta?.roles || []).map((r) => (
                          <SelectItem key={r.key} value={r.key}>{r.label}{r.enabled ? "" : " (disabled by default)"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Framework scope</Label>
                    <div className="flex flex-wrap gap-2">
                      {FRAMEWORKS.map((f) => (
                        <button
                          key={f}
                          data-testid={`grant-framework-${f}`}
                          onClick={() =>
                            setForm({
                              ...form,
                              frameworks: form.frameworks.includes(f)
                                ? form.frameworks.filter((x) => x !== f)
                                : [...form.frameworks, f],
                            })
                          }
                          className={`border px-3 py-1.5 text-xs transition-colors ${
                            form.frameworks.includes(f) ? "border-foreground bg-foreground text-background" : "border-border"
                          }`}
                        >
                          {f}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">Leave empty to use role defaults.</p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Panchatanthra section scope</Label>
                    <div className="flex flex-wrap gap-2">
                      {PT_ELEMENTS.map((e) => (
                        <button
                          key={e.code}
                          data-testid={`grant-element-${e.code}`}
                          onClick={() =>
                            setForm({
                              ...form,
                              element_codes: form.element_codes.includes(e.code)
                                ? form.element_codes.filter((x) => x !== e.code)
                                : [...form.element_codes, e.code],
                            })
                          }
                          className={`border px-3 py-1.5 text-xs transition-colors ${
                            form.element_codes.includes(e.code) ? "border-foreground bg-foreground text-background" : "border-border"
                          }`}
                        >
                          {e.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Leave empty for all five sections. Pick sections to restrict a team (e.g. finance team = Financial Performance only).
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label className="label-caps">Confidentiality ceiling</Label>
                    <Select value={form.confidentiality_max} onValueChange={(v) => setForm({ ...form, confidentiality_max: v })}>
                      <SelectTrigger data-testid="grant-confidentiality-select" className="rounded-none"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        {[1, 2, 3].map((l) => <SelectItem key={l} value={String(l)}>{["", "General", "Sensitive", "Restricted"][l]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button data-testid="create-grant-submit" className="w-full rounded-none" disabled={!form.user_id} onClick={createGrant}>
                    Issue grant
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="panel divide-y divide-border" data-testid="grants-list">
        {grants.map((g) => (
          <div key={g.id} className="flex flex-wrap items-start justify-between gap-4 px-5 py-5">
            <div className="min-w-0">
              <p className="text-sm">{g.user?.name} <span className="text-muted-foreground">· {g.user?.email}</span></p>
              <p className="label-caps mt-1">{g.role} · {(g.frameworks || []).join(", ") || "no framework"}</p>
              {(g.element_codes || []).length > 0 && (
                <p className="label-caps mt-1 text-deepblue" data-testid={`grant-elements-${g.id}`}>
                  Sections: {(g.element_codes || []).join(", ")}
                </p>
              )}
              <div className="mt-3 flex flex-wrap gap-1.5">
                {(g.actions || []).map((a) => (
                  <span key={a} className="border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase">{a}</span>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <ConfBadge level={g.confidentiality_max} />
              <label className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em]">
                Enabled
                <Switch
                  data-testid={`grant-enabled-${g.id}`}
                  checked={!!g.enabled}
                  onCheckedChange={async (v) => {
                    await api.put(`/tenants/${tenantId}/grants/${g.id}`, { enabled: v }).catch((e) => toast.error(errMsg(e)));
                    load();
                  }}
                />
              </label>
              <Button
                data-testid={`revoke-grant-${g.id}`}
                size="sm"
                variant="ghost"
                className="rounded-none text-[10px] uppercase tracking-[0.15em] text-destructive"
                onClick={async () => {
                  await api.delete(`/tenants/${tenantId}/grants/${g.id}`).catch((e) => toast.error(errMsg(e)));
                  load();
                }}
              >
                Revoke
              </Button>
            </div>
          </div>
        ))}
        {grants.length === 0 && <EmptyState testid="grants-empty" title="No grants" message="Issue the first scoped grant for this engagement." />}
      </div>
    </div>
  );
}
